import {
  faClinicMedical,
  faHandHoldingHeart,
  faLayerGroup,
  faPrescriptionBottleAlt,
  faSun,
  faUser,
  faUserCheck,
  faUserClock,
  faUserNurse,
  faUserShield,
  faUserTimes,
  faUsersCog
} from '@fortawesome/free-solid-svg-icons';

/**
 * Shared vocabulary for user self-registration (userregistrations.md): the
 * public form, the admin approval queue and the sidebar badge all read their
 * labels, colour tones and icons from here, so the applicant and the reviewer
 * always see the same words for the same thing.
 */

export const SUPPORT_EMAIL = 'usermanagement@mapyourmeds.com';

// Backend R4.6: the set-password link in the welcome email expires after 72h.
export const INVITE_LINK_TTL_HOURS = 72;

// Applicants who haven't heard back by then are told to contact their agency
// administrator — MapYourMeds can't approve access on an agency's behalf.
export const ADMIN_RESPONSE_HOURS = 72;

/* ------------------------------------------------------------------------ */
/*                                  Roles                                   */
/* ------------------------------------------------------------------------ */

// Roles rank "lower number = more privileged".
export const ROLE_META = {
  1: { label: 'System Admin', tone: 'secondary', icon: faUserShield },
  2: { label: 'Pharmacy Admin', tone: 'teal', icon: faClinicMedical },
  3: { label: 'Pharmacy Staff', tone: 'teal', icon: faPrescriptionBottleAlt },
  4: {
    label: 'Group Admin',
    tone: 'purple',
    icon: faUsersCog,
    description: 'Manages users and settings for the whole group.'
  },
  5: {
    label: 'Facility Staff',
    tone: 'primary',
    icon: faUserNurse,
    description:
      'Works at one or more facilities: med pass, orders and daily care.'
  },
  6: {
    label: 'Day Program User',
    tone: 'warning',
    icon: faSun,
    description: 'Supports people who attend a day program.'
  },
  7: {
    label: 'SharedLiving Caregiver',
    tone: 'pink',
    icon: faHandHoldingHeart,
    description: 'Cares for someone living in a shared-living home.'
  }
};

// D6: the public form may only offer these. The server clamps anything else.
export const PUBLIC_REQUESTABLE_ROLE_IDS = [5, 6, 7];

export const getRoleMeta = roleId =>
  ROLE_META[Number(roleId)] || {
    label: roleId ? `Role ${roleId}` : 'Not specified',
    tone: 'secondary',
    icon: faUser
  };

/* ------------------------------------------------------------------------ */
/*                                 Statuses                                 */
/* ------------------------------------------------------------------------ */

export const REGISTRATION_STATUS_TABS = [
  'pending',
  'approved',
  'rejected',
  'all'
];

export const STATUS_META = {
  pending: {
    label: 'Pending',
    tone: 'warning',
    icon: faUserClock,
    emptyTitle: 'All caught up',
    emptyText: 'No requests are waiting for review.'
  },
  approved: {
    label: 'Approved',
    tone: 'success',
    icon: faUserCheck,
    emptyTitle: 'No approved requests yet',
    emptyText: 'Approved requests appear here.'
  },
  rejected: {
    label: 'Rejected',
    tone: 'danger',
    icon: faUserTimes,
    emptyTitle: 'No rejected requests',
    emptyText: 'Rejected requests and their reasons appear here.'
  },
  all: {
    label: 'All',
    tone: 'primary',
    icon: faLayerGroup,
    emptyTitle: 'No registration requests yet',
    emptyText: 'Requests from the registration page appear here.'
  }
};

export const isKnownStatusTab = status =>
  REGISTRATION_STATUS_TABS.includes(String(status || '').toLowerCase());

export const getStatusMeta = status => {
  const key = String(status || '').toLowerCase();
  if (STATUS_META[key]) {
    return STATUS_META[key];
  }
  return {
    label: key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Unknown',
    tone: 'secondary',
    icon: faUser
  };
};

/* ------------------------------------------------------------------------ */
/*                          Reviewer access (UI only)                       */
/* ------------------------------------------------------------------------ */

// U1.3 / U6.3: the queue is offered to roles 1–4. This is a usability gate,
// NOT access control — the backend authorizes every queue call (R3.3, R5.5).
export const REGISTRATION_REVIEWER_ROLE_IDS = [1, 2, 3, 4];

export const getLoggedInRoleId = () => Number(localStorage.getItem('roleId'));

export const canReviewRegistrations = (roleId = getLoggedInRoleId()) =>
  REGISTRATION_REVIEWER_ROLE_IDS.includes(Number(roleId));

// Email domains link registrations to any group, so only pharmacy roles.
export const EMAIL_DOMAIN_MANAGER_ROLE_IDS = [1, 2, 3];

export const canManageEmailDomains = (roleId = getLoggedInRoleId()) =>
  EMAIL_DOMAIN_MANAGER_ROLE_IDS.includes(Number(roleId));

/* ------------------------------------------------------------------------ */
/*                  Cross-component "the queue changed" signal               */
/* ------------------------------------------------------------------------ */

// The queue page broadcasts fresh counts so the sidebar badge (U3.6) stays in
// sync without its own polling.
export const REGISTRATIONS_CHANGED_EVENT = 'mym:registrations-changed';

export const announceRegistrationsChanged = (detail = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent(REGISTRATIONS_CHANGED_EVENT, { detail })
    );
  } catch (error) {
    // CustomEvent is unavailable only in very old browsers — the badge will
    // simply refresh on the next window focus instead.
  }
};
